/**
 * Sparkline — pure-SVG line chart for ephemeral probe history.
 *
 * Accepts either:
 *   - Legacy: `points: readonly number[]` (backward-compat)
 *   - New: `points: ReadonlyArray<{ ts: number; value: number }>`
 *
 * When the new form is used, an optional `onHover` callback is fired with
 * the nearest point when the cursor is over the SVG, and a crosshair
 * line + dot is rendered at that position.
 *
 * Defensive against three edge cases:
 *   - **No points** → renders an empty `<svg>`.
 *   - **One point** → renders a centered dot.
 *   - **Flat series** → draws a horizontal midline.
 *
 * Phase 2: default stroke is now the Abyss `--accent` token (coral).
 * A soft area-fill polygon uses `--accent-soft` beneath the line.
 */
import { useCallback, useMemo, useRef, useState } from 'react';

/** A timestamped data point — the new preferred form for points. */
export interface SparklinePoint {
  ts: number;
  value: number;
}

export interface SparklineProps {
  /**
   * Most-recent-last list of values.
   * Accepts either `number[]` (legacy, backward-compat) or
   * `SparklinePoint[]` (new, required for hover scrubber).
   */
  points: readonly number[] | ReadonlyArray<SparklinePoint>;
  /** Pixel width of the rendered <svg>. Defaults to 120. */
  width?: number;
  /** Pixel height of the rendered <svg>. Defaults to 32. */
  height?: number;
  /**
   * Stroke colour. Defaults to `var(--accent)` (Abyss coral token).
   * Pass `'var(--accent-cyan)'` for the secondary cyan variant.
   */
  stroke?: string;
  /** Optional className passed to the root <svg>. */
  className?: string;
  /** Accessible label (purely advisory; sparklines are decorative). */
  ariaLabel?: string;
  /**
   * Called with the nearest point when the cursor enters or moves over the
   * SVG, and with `null` when it leaves. Only fired when `points` contains
   * `SparklinePoint` objects (not legacy numbers).
   */
  onHover?: (point: SparklinePoint | null) => void;
}

// Fixed viewBox keeps coordinate math simple regardless of render size.
const VB_W = 120;
const VB_H = 32;
// 1px padding so the stroke doesn't get clipped by the edges of the viewBox.
const PAD = 1;

function isPointArray(
  points: readonly number[] | ReadonlyArray<SparklinePoint>,
): points is ReadonlyArray<SparklinePoint> {
  return points.length > 0 && typeof (points[0] as SparklinePoint).value === 'number';
}

/** Normalise the input to a flat `number[]` for path computation. */
function toValues(points: readonly number[] | ReadonlyArray<SparklinePoint>): number[] {
  if (points.length === 0) return [];
  if (isPointArray(points)) return points.map((p) => p.value);
  return points as number[];
}

interface Plot {
  kind: 'empty' | 'dot' | 'flat' | 'line';
  d?: string;
  cx?: number;
  cy?: number;
  /** Per-point (x, y) coords — only set for `line` kind. */
  coords?: Array<{ x: number; y: number }>;
}

function plot(values: number[]): Plot {
  if (values.length === 0) return { kind: 'empty' };

  const innerW = VB_W - PAD * 2;
  const innerH = VB_H - PAD * 2;

  if (values.length === 1) {
    return { kind: 'dot', cx: VB_W / 2, cy: VB_H / 2 };
  }

  let min = values[0] ?? 0;
  let max = min;
  for (const v of values) {
    if (v < min) min = v;
    if (v > max) max = v;
  }

  if (min === max) {
    const y = VB_H / 2;
    return { kind: 'flat', d: `M ${PAD} ${y} L ${VB_W - PAD} ${y}` };
  }

  const range = max - min;
  const stepX = innerW / (values.length - 1);
  const coords = values.map((v, i) => ({
    x: PAD + stepX * i,
    y: PAD + innerH - ((v - min) / range) * innerH,
  }));
  const d = coords
    .map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(2)} ${c.y.toFixed(2)}`)
    .join(' ');
  return { kind: 'line', d, coords };
}

export function Sparkline({
  points,
  width = 120,
  height = 32,
  stroke = 'var(--accent)',
  className,
  ariaLabel,
  onHover,
}: SparklineProps) {
  const values = useMemo(() => toValues(points), [points]);
  const p = useMemo(() => plot(values), [values]);

  // Crosshair state — null when cursor is off the SVG.
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!onHover || !isPointArray(points) || p.kind !== 'line' || !p.coords) return;
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      // Map client x → viewBox x.
      const vbX = ((e.clientX - rect.left) / rect.width) * VB_W;
      // Find the nearest coord by x distance.
      let nearestIdx = 0;
      let minDx = Math.abs(p.coords[0]!.x - vbX);
      for (let i = 1; i < p.coords.length; i++) {
        const dx = Math.abs(p.coords[i]!.x - vbX);
        if (dx < minDx) {
          minDx = dx;
          nearestIdx = i;
        }
      }
      setHoverIdx(nearestIdx);
      const pt = (points as ReadonlyArray<SparklinePoint>)[nearestIdx];
      if (pt) onHover(pt);
    },
    [onHover, points, p],
  );

  const handleMouseLeave = useCallback(() => {
    if (!onHover) return;
    setHoverIdx(null);
    onHover(null);
  }, [onHover]);

  // Crosshair elements — only rendered when hovering over a line chart.
  const crosshair = useMemo(() => {
    if (hoverIdx === null || p.kind !== 'line' || !p.coords) return null;
    const coord = p.coords[hoverIdx];
    if (!coord) return null;
    return (
      <>
        <line
          x1={coord.x}
          y1={PAD}
          x2={coord.x}
          y2={VB_H - PAD}
          stroke={stroke}
          strokeWidth={0.75}
          strokeOpacity={0.5}
          strokeDasharray="2 1"
          data-testid="sparkline-crosshair"
        />
        <circle
          cx={coord.x}
          cy={coord.y}
          r={2}
          fill={stroke}
          data-testid="sparkline-hover-dot"
        />
      </>
    );
  }, [hoverIdx, p, stroke]);

  return (
    <svg
      ref={svgRef}
      role="img"
      aria-label={ariaLabel ?? `sparkline (${values.length} samples)`}
      width={width}
      height={height}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="none"
      className={className}
      data-testid="sparkline"
      data-kind={p.kind}
      data-points={values.length}
      onMouseMove={onHover ? handleMouseMove : undefined}
      onMouseLeave={onHover ? handleMouseLeave : undefined}
    >
      {p.kind === 'dot' ? (
        <circle cx={p.cx} cy={p.cy} r={1.5} fill={stroke} />
      ) : null}
      {p.kind === 'flat' || p.kind === 'line' ? (
        <>
          {/* Soft area fill beneath the line */}
          {p.kind === 'line' && p.coords && p.coords.length > 0 ? (
            <path
              d={
                `${p.d ?? ''} ` +
                `L ${(p.coords[p.coords.length - 1]?.x ?? VB_W - PAD).toFixed(2)} ${VB_H - PAD} ` +
                `L ${(p.coords[0]?.x ?? PAD).toFixed(2)} ${VB_H - PAD} Z`
              }
              fill="var(--accent-soft)"
              stroke="none"
            />
          ) : null}
          <path
            d={p.d}
            fill="none"
            stroke={stroke}
            strokeWidth={1.25}
            strokeLinejoin="round"
            strokeLinecap="round"
            data-testid="sparkline-line"
          />
        </>
      ) : null}
      {crosshair}
    </svg>
  );
}
